import React, { useEffect, useRef } from 'react';
import './CursorPixie.css';

export default function CursorPixie() {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let width = 0;
    let height = 0;

    function resize() {
      const dpr = window.devicePixelRatio || 1;
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    resize();
    window.addEventListener('resize', resize);

    const colors = ['#FF5A7F', '#FF82B3', '#FF9EC9', '#E7C3FF', '#FFE297'];

    function spawn(x, y) {
      // slightly larger and a bit more particles
      const count = 7; // ~20% more particles than before
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 0.25 + Math.random() * 0.9; // a touch faster
        const vx = Math.cos(angle) * speed;
        const vy = Math.sin(angle) * speed;
        const size = (0.9 + Math.random() * 1.1) * 2; // additional 20% (total ~44% larger)
        const life = 70 + Math.floor(Math.random() * 50); // longer life for extended trail
        const color = colors[Math.floor(Math.random() * colors.length)];
        particlesRef.current.push({ x, y, vx, vy, size, life, age: 0, color });
      }
      // increase cap slightly to allow more particles but still bounded
      if (particlesRef.current.length > 1600) particlesRef.current.splice(0, 350);
    }

    let lastMove = { x: -9999, y: -9999 };

    function onMove(e) {
      const x = e.clientX;
      const y = e.clientY;
      // spawn when mouse moves some distance to avoid huge bursts
      const dx = x - lastMove.x;
      const dy = y - lastMove.y;
      const dist2 = dx * dx + dy * dy;
      if (dist2 > 4) {
        spawn(x, y);
        lastMove = { x, y };
      }
    }

    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', (ev) => {
      const t = ev.touches && ev.touches[0];
      if (t) onMove({ clientX: t.clientX, clientY: t.clientY });
    }, { passive: true });

    function update() {
      ctx.clearRect(0, 0, width, height);
      const p = particlesRef.current;
      for (let i = p.length - 1; i >= 0; i--) {
        const part = p[i];
        part.x += part.vx;
        part.y += part.vy;
        part.vy += 0.03; // gravity subtle
        part.vx *= 0.99;
        part.vy *= 0.99;
        part.age++;
        const t = part.age / part.life;
        const alpha = Math.max(1 - t, 0);

        // draw tiny glow (very subtle for sand-like look)
        ctx.beginPath();
        ctx.fillStyle = part.color;
        ctx.globalAlpha = 0.10 * alpha; // slightly stronger glow
        ctx.arc(part.x, part.y, part.size * 1.44, 0, Math.PI * 2); // 20% larger glow radius
        ctx.fill();

        // subtle dark outline for visibility on light backgrounds
        const coreRadius = Math.max(0.3, part.size * (1 - t * 0.9));
        ctx.beginPath();
        ctx.fillStyle = 'rgba(0,0,0,0.18)';
        ctx.globalAlpha = 0.30 * alpha;
        ctx.arc(part.x, part.y, coreRadius + 0.9, 0, Math.PI * 2);
        ctx.fill();

        // draw core (very small, pale)
        ctx.beginPath();
        ctx.fillStyle = part.color;
        ctx.globalAlpha = 0.66 * alpha;
        ctx.arc(part.x, part.y, coreRadius, 0, Math.PI * 2);
        ctx.fill();

        if (part.age >= part.life) {
          p.splice(i, 1);
        }
      }
      ctx.globalAlpha = 1;
      rafRef.current = requestAnimationFrame(update);
    }

    rafRef.current = requestAnimationFrame(update);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <canvas className="pixie-canvas" ref={canvasRef} />
  );
}
